import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Popconfirm,
  Avatar,
  Row,
  Col,
  Space,
  Alert,
  Descriptions,
  Spin,
  Empty,
  Segmented,
  Skeleton,
  ConfigProvider,
} from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  BookOutlined,
  UserOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  CrownOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  UnorderedListOutlined,
  PaperClipOutlined,
  KeyOutlined,
  LockOutlined,
  UnlockOutlined,
  SearchOutlined,
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  ContactsOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { courseApi } from '../../../api/course.api.js';
import { authApi } from '../../../api/auth.api.js';
import { lessonApi } from '../../../api/lesson.api.js';
import { analyticsApi } from '../../../api/analytics.api.js';
import { subscriptionApi } from '../../../api/subscription.api.js';
import { planApi } from '../../../api/plan.api.js';
import { paymentApi } from '../../../api/payment.api.js';
import { supportChatApi } from '../../../api/support-chat.api.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { AdminLeadsPanel } from '../components/AdminLeadsPanel.jsx';
import { AdminSupportChatPanel } from '../components/AdminSupportChatPanel.jsx';
import { AdminShell, adminTheme } from '../components/AdminShell.jsx';
import { KpiStrip, RevenueTrend, HsdStamp, StatusPill, Panel, daysUntil } from '../components/AdminUi.jsx';
import { AdminUserQuizResults } from '../components/AdminUserQuizResults.jsx';
import { COURSE_THUMBNAILS } from '../../courses/components/CourseCard.jsx';
import {
  PROVINCES,
  getWardsByProvinceCode,
  getProvinceCodeByName,
} from '../../../constants/vietnamAdministrativeDivisions.js';

const PROVINCE_OPTIONS = PROVINCES.map((p) => ({
  label: p.fullName,
  value: p.name,
  code: p.code,
}));

export function AdminDashboardPage() {
  const navigate = useNavigate();
  // ADMIN thấy toàn bộ; INSTRUCTOR chỉ thấy khóa học của mình và thống kê khóa mình (YC-ADM-09)
  const { user: currentUser, isAdmin, logout } = useAuth();
  const [activeMenuKey, setActiveMenuKey] = useState(isAdmin ? 'subscriptions' : 'courses');

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subStats, setSubStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Đơn hàng / giao dịch thanh toán QR chuyển khoản (SePay; đơn cũ trước 12/10/2026 là PayOS)
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [orderSearchText, setOrderSearchText] = useState('');
  const [selectedOrderPayload, setSelectedOrderPayload] = useState(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  // Số cuộc chat đang chờ người trả lời, hiện trên thanh bên
  const [waitingChats, setWaitingChats] = useState(0);

  // Modal State tạo khóa học
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [courseForm] = Form.useForm();
  const [creatingCourse, setCreatingCourse] = useState(false);

  // Modal State thêm bài học
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [selectedCourseForLesson, setSelectedCourseForLesson] = useState(null);
  const [lessonForm] = Form.useForm();
  const [creatingLesson, setCreatingLesson] = useState(false);

  // Modal State Quản lý & Xóa bài học trong khóa học
  const [isManageLessonsModalOpen, setIsManageLessonsModalOpen] = useState(false);
  const [selectedCourseForManage, setSelectedCourseForManage] = useState(null);
  const [courseLessonsList, setCourseLessonsList] = useState([]);
  const [loadingCourseLessons, setLoadingCourseLessons] = useState(false);

  // Modal State Cấp / Nâng cấp gói học cho học viên
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [grantForm] = Form.useForm();
  const [granting, setGranting] = useState(false);

  // Modal State Gia hạn gói
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [selectedSubForExtend, setSelectedSubForExtend] = useState(null);
  const [extendForm] = Form.useForm();
  const [extending, setExtending] = useState(false);

  // Modal State Đổi mật khẩu cho học viên / nhà thuốc
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [resetPasswordForm] = Form.useForm();
  const [resettingPassword, setResettingPassword] = useState(false);
  const [userSearchText, setUserSearchText] = useState('');

  // Modal State Xem chi tiết học viên / nhà thuốc
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
  const [selectedUserForDetail, setSelectedUserForDetail] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);

  // Modal State Sửa thông tin học viên / nhà thuốc
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [editUserForm] = Form.useForm();
  const [updatingUser, setUpdatingUser] = useState(false);
  const [editWardOptions, setEditWardOptions] = useState([]);

  // Khóa / mở khóa tài khoản
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [forceDeleteUser, setForceDeleteUser] = useState(null);
  const [forceDeleteEmail, setForceDeleteEmail] = useState('');

  const fetchAdminData = useCallback(async () => {
    setLoading(true);
    try {
      if (!isAdmin) {
        // Giảng viên: không gọi API người dùng, đơn hàng, hội viên (chỉ ADMIN được gọi)
        const [coursesRes, catsRes, statsRes] = await Promise.all([
          courseApi.getCourses({ limit: 50 }),
          courseApi.getCategories(),
          analyticsApi.getDashboardStats().catch(() => ({ data: null })),
        ]);
        setCourses((coursesRes.data || []).filter((c) => c.instructorId === currentUser?.id));
        setCategories(catsRes.data || []);
        setStats(statsRes.data);
        return;
      }

      const [coursesRes, catsRes, usersRes, statsRes, subsRes, plansRes, ordersRes] = await Promise.all([
        courseApi.getCourses({ limit: 50 }),
        courseApi.getCategories(),
        authApi.getUsers({ limit: 50 }),
        analyticsApi.getDashboardStats().catch(() => ({ data: null })),
        subscriptionApi.getAllSubscriptions({ limit: 100 }).catch(() => ({ data: [], meta: null })),
        planApi.getAllPlans('all').catch(() => ({ data: [] })),
        paymentApi.getAllOrders({ limit: 100 }).catch(() => ({ data: [], meta: null })),
      ]);

      setCourses(coursesRes.data || []);
      setCategories(catsRes.data || []);
      setUsers(usersRes.data || []);
      setStats(statsRes.data);
      setSubscriptions(subsRes.data || []);
      setSubStats(subsRes.meta?.stats || null);
      setPlans(plansRes.data || []);
      setOrders(ordersRes.data || []);
      setOrderStats(ordersRes.meta?.stats || null);
    } catch (err) {
      toast.error(err.message || 'Lỗi khi tải dữ liệu quản trị');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, currentUser?.id]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  useEffect(() => {
    if (!isAdmin) return;
    supportChatApi
      .adminListConversations({ filter: 'waiting', page: 1, pageSize: 1 })
      .then((res) => setWaitingChats(res.meta?.waiting || 0))
      .catch(() => {});
  }, [isAdmin]);

  const formatPrice = (price) => {
    if (price === 0) return '0đ (Miễn phí)';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
  };

  // Tạo khóa học mới
  const handleCreateCourse = async (values) => {
    setCreatingCourse(true);
    try {
      await courseApi.createCourse(values);
      toast.success('Đã tạo khóa học mới thành công!');
      setIsCourseModalOpen(false);
      courseForm.resetFields();
      fetchAdminData();
    } catch (err) {
      toast.error(err.message || 'Lỗi khi tạo khóa học');
    } finally {
      setCreatingCourse(false);
    }
  };

  // Xóa khóa học
  const handleDeleteCourse = async (id) => {
    try {
      await courseApi.deleteCourse(id);
      toast.success('Đã xóa khóa học!');
      fetchAdminData();
    } catch (err) {
      toast.error(err.message || 'Lỗi khi xóa khóa học');
    }
  };

  // State lưu trữ mã module gợi ý tự động (+1) và moduleId
  const [suggestedCode, setSuggestedCode] = useState('');
  const [currentModuleId, setCurrentModuleId] = useState(null);

  // Mở modal thêm bài học với tự động tính toán Mã Module (Prefix + maxNum + 1)
  const handleOpenAddLesson = async (course) => {
    setSelectedCourseForLesson(course);
    lessonForm.resetFields();

    let prefix = 'M';
    let maxNum = 0;
    let targetModuleId = null;

    try {
      const res = await courseApi.getCourseDetails(course.id);
      if (res.data?.syllabus && res.data.syllabus.length > 0) {
        targetModuleId = res.data.syllabus[0].id;
        res.data.syllabus.forEach((mod) => {
          if (mod.lessons) {
            mod.lessons.forEach((l) => {
              if (l.code) {
                const match = l.code.match(/^([A-Za-z]+)(\d+)$/);
                if (match) {
                  prefix = match[1].toUpperCase();
                  const num = parseInt(match[2], 10);
                  if (num > maxNum) maxNum = num;
                }
              }
            });
          }
        });
      }
    } catch (e) {
      // Fallback nếu không tải được chi tiết
    }

    if (maxNum === 0) {
      const t = (course.title || '').toLowerCase();
      if (t.includes('bán hàng') || t.includes('tư vấn') || t.includes('sales')) prefix = 'S';
      else if (t.includes('marketing') || t.includes('khảo sát')) prefix = 'M';
      else if (t.includes('vị trí') || t.includes('mặt bằng') || t.includes('vốn')) prefix = 'V';
      else if (t.includes('nhân sự') || t.includes('tuyển dụng')) prefix = 'N';
      else if (t.includes('công nghệ') || t.includes('ai') || t.includes('phần mềm')) prefix = 'C';
      else if (t.includes('pháp lý') || t.includes('giấy phép') || t.includes('gpp')) prefix = 'P';
      else if (t.includes('dược') || t.includes('chuyên môn') || t.includes('lâm sàng')) prefix = 'D';
    }

    const nextCode = `${prefix}${maxNum + 1}`;
    setSuggestedCode(nextCode);
    setCurrentModuleId(targetModuleId);

    lessonForm.setFieldsValue({
      code: nextCode,
      estimatedMinutes: 45,
      lessonType: 'ARTICLE',
      contentMarkdown: '## Nội dung bài giảng...',
    });

    setIsLessonModalOpen(true);
  };

  // Tạo bài học mới
  const handleCreateLesson = async (values) => {
    if (!selectedCourseForLesson) return;
    setCreatingLesson(true);

    const finalCode = values.code || suggestedCode;
    const formattedAttachments = (values.attachments || []).map((att, idx) => ({
      ...att,
      code: `${finalCode}-TL${String(idx + 1).padStart(2, '0')}`,
    }));

    try {
      await lessonApi.createLesson({
        ...values,
        code: finalCode,
        moduleId: values.moduleId || currentModuleId || undefined,
        attachments: formattedAttachments,
        courseId: selectedCourseForLesson.id,
      });
      toast.success('Đã thêm bài học vào khóa thành công!');
      setIsLessonModalOpen(false);
      lessonForm.resetFields();
      fetchAdminData();
      if (selectedCourseForManage && selectedCourseForManage.id === selectedCourseForLesson.id) {
        handleOpenManageLessons(selectedCourseForManage);
      }
    } catch (err) {
      toast.error(err.message || 'Lỗi khi thêm bài học');
    } finally {
      setCreatingLesson(false);
    }
  };

  // Mở modal quản lý & xóa bài học của 1 khóa
  const handleOpenManageLessons = async (course) => {
    setSelectedCourseForManage(course);
    setIsManageLessonsModalOpen(true);
    setLoadingCourseLessons(true);
    try {
      const res = await courseApi.getCourseDetails(course.id);
      const allLessons = [];
      if (res.data?.syllabus) {
        res.data.syllabus.forEach((mod) => {
          if (mod.lessons) {
            mod.lessons.forEach((l) => {
              allLessons.push({ ...l, moduleTitle: mod.title });
            });
          }
        });
      }
      const parseCodeNum = (l) => {
        if (l.code) {
          const m = l.code.match(/\d+/);
          if (m) return parseInt(m[0], 10);
        }
        return l.order || 9999;
      };
      allLessons.sort((a, b) => parseCodeNum(a) - parseCodeNum(b));
      setCourseLessonsList(allLessons);
    } catch (err) {
      toast.error('Không thể tải danh sách bài học của khóa');
    } finally {
      setLoadingCourseLessons(false);
    }
  };

  // Xóa 1 module bài học trong khóa học
  const handleDeleteLessonInCourse = async (lessonId) => {
    try {
      await lessonApi.deleteLesson(lessonId);
      toast.success('Đã xóa module bài học khỏi khóa đào tạo thành công!');
      setCourseLessonsList((prev) => prev.filter((l) => l.id !== lessonId));
      fetchAdminData();
    } catch (err) {
      toast.error(err.message || 'Lỗi khi xóa bài học');
    }
  };

  // Cấp mới / Nâng cấp gói học cho học viên
  const handleGrantSubscription = async (values) => {
    setGranting(true);
    try {
      await subscriptionApi.grantSubscription(values);
      toast.success('Đã kích hoạt gói học viên thành công!');
      setIsGrantModalOpen(false);
      grantForm.resetFields();
      fetchAdminData();
    } catch (err) {
      toast.error(err.message || 'Lỗi khi cấp gói học viên');
    } finally {
      setGranting(false);
    }
  };

  // Mở modal gia hạn
  const handleOpenExtendModal = (sub) => {
    setSelectedSubForExtend(sub);
    extendForm.resetFields();
    extendForm.setFieldsValue({ extendMonths: 6, status: sub.status });
    setIsExtendModalOpen(true);
  };

  // Gia hạn gói học
  const handleExtendSubscription = async (values) => {
    if (!selectedSubForExtend) return;
    setExtending(true);
    try {
      await subscriptionApi.updateSubscription(selectedSubForExtend.id, values);
      toast.success('Đã gia hạn gói học viên thành công!');
      setIsExtendModalOpen(false);
      fetchAdminData();
    } catch (err) {
      toast.error(err.message || 'Lỗi khi gia hạn gói');
    } finally {
      setExtending(false);
    }
  };

  // Mở modal đặt lại mật khẩu cho học viên / nhà thuốc
  const handleOpenResetPasswordModal = (user) => {
    setSelectedUserForReset(user);
    resetPasswordForm.resetFields();
    setIsResetPasswordModalOpen(true);
  };

  // Tạo ngẫu nhiên mật khẩu bảo mật & dễ nhớ
  const handleGenerateRandomPassword = () => {
    const prefixes = ['Pharma', 'Thuoc', 'DuocSi', 'Gpp', 'Lms'];
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const newPwd = `${prefix}@${randomNum}`;
    resetPasswordForm.setFieldsValue({
      newPassword: newPwd,
      confirmPassword: newPwd,
    });
    toast.info(`Đã tạo gợi ý mật khẩu: ${newPwd}`);
  };

  // Xác nhận đổi mật khẩu
  const handleResetPasswordSubmit = async (values) => {
    if (!selectedUserForReset?.id) {
      toast.error('Không tìm thấy thông tin tài khoản');
      return;
    }

    setResettingPassword(true);
    try {
      await authApi.resetUserPassword(selectedUserForReset.id, values.newPassword);
      toast.success(
        `Đã cập nhật mật khẩu mới cho ${selectedUserForReset.fullName || selectedUserForReset.email} thành công!`
      );
      setIsResetPasswordModalOpen(false);
      resetPasswordForm.resetFields();
    } catch (err) {
      toast.error(err.message || 'Lỗi khi cập nhật mật khẩu mới');
    } finally {
      setResettingPassword(false);
    }
  };

  // Mở modal xem chi tiết học viên / nhà thuốc
  const handleOpenUserDetailModal = async (user) => {
    setIsUserDetailModalOpen(true);
    setLoadingUserDetail(true);
    setSelectedUserForDetail(user);
    try {
      const res = await authApi.getUserDetail(user.id);
      setSelectedUserForDetail(res.data);
    } catch (err) {
      toast.error(err.message || 'Lỗi khi tải chi tiết tài khoản');
    } finally {
      setLoadingUserDetail(false);
    }
  };

  // Đổi Tỉnh/Thành trong form sửa -> nạp lại danh sách Phường/Xã tương ứng
  const handleEditProvinceChange = (value, option) => {
    editUserForm.setFieldsValue({ district: undefined });
    const wards = getWardsByProvinceCode(option?.code);
    setEditWardOptions(wards.map((w) => ({ label: w.fullName, value: w.fullName })));
  };

  // Mở modal sửa thông tin học viên / nhà thuốc
  const handleOpenEditUserModal = (user) => {
    setSelectedUserForEdit(user);
    const provinceCode = getProvinceCodeByName(user.province);
    setEditWardOptions(
      provinceCode
        ? getWardsByProvinceCode(provinceCode).map((w) => ({ label: w.fullName, value: w.fullName }))
        : []
    );
    editUserForm.setFieldsValue({
      fullName: user.fullName,
      phone: user.phone,
      role: user.role,
      pharmacyName: user.pharmacyName,
      position: user.position,
      province: user.province,
      district: user.district,
      pharmacyScale: user.pharmacyScale,
      zaloNumber: user.zaloNumber,
      leadSource: user.leadSource,
    });
    setIsEditUserModalOpen(true);
  };

  // Xác nhận sửa thông tin học viên / nhà thuốc
  const handleUpdateUserSubmit = async (values) => {
    if (!selectedUserForEdit?.id) return;
    setUpdatingUser(true);
    try {
      const res = await authApi.updateUser(selectedUserForEdit.id, values);
      toast.success('Đã cập nhật thông tin học viên thành công!');
      setUsers((prev) =>
        prev.map((u) => (u.id === selectedUserForEdit.id ? { ...u, ...res.data } : u))
      );
      setIsEditUserModalOpen(false);
      editUserForm.resetFields();
    } catch (err) {
      toast.error(err.message || 'Lỗi khi cập nhật thông tin học viên');
    } finally {
      setUpdatingUser(false);
    }
  };

  // Khóa / mở khóa tài khoản học viên / nhà thuốc
  const handleToggleUserStatus = async (user) => {
    const nextIsActive = !user.isActive;
    setTogglingUserId(user.id);
    try {
      const res = await authApi.setUserStatus(user.id, nextIsActive);
      toast.success(
        nextIsActive
          ? `Đã mở khóa tài khoản ${user.fullName || user.email}`
          : `Đã khóa tài khoản ${user.fullName || user.email}`
      );
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...res.data } : u)));
    } catch (err) {
      toast.error(err.message || 'Lỗi khi cập nhật trạng thái tài khoản');
    } finally {
      setTogglingUserId(null);
    }
  };

  // Xóa hẳn tài khoản học viên / nhà thuốc (máy chủ chặn tài khoản đã có đơn thanh toán)
  // Tài khoản có đơn đã thanh toán (tài khoản thử nghiệm): mở hộp gõ lại email để xóa luôn cả đơn hàng
  const handleDeleteUser = async (user, confirmEmail) => {
    setDeletingUserId(user.id);
    try {
      await authApi.deleteUser(user.id, confirmEmail);
      toast.success(`Đã xóa tài khoản ${user.fullName || user.email}`);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      setForceDeleteUser(null);
    } catch (err) {
      if (err.details?.paidOrders && !confirmEmail) {
        setForceDeleteEmail('');
        setForceDeleteUser({ ...user, paidOrders: err.details.paidOrders });
      } else {
        toast.error(err.message || 'Lỗi khi xóa tài khoản');
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  // Lọc danh sách học viên / nhà thuốc theo từ khoá tìm kiếm
  const filteredUsers = users.filter((u) => {
    if (!userSearchText.trim()) return true;
    const q = userSearchText.toLowerCase().trim();
    return (
      (u.fullName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q) ||
      (u.pharmacyName || '').toLowerCase().includes(q) ||
      (u.province || '').toLowerCase().includes(q)
    );
  });

  // Lọc danh sách đơn hàng theo tên/email/SĐT/tên nhà thuốc học viên
  const filteredOrders = orders.filter((o) => {
    if (!orderSearchText.trim()) return true;
    const q = orderSearchText.toLowerCase().trim();
    return (
      (o.user?.fullName || '').toLowerCase().includes(q) ||
      (o.user?.email || '').toLowerCase().includes(q) ||
      (o.user?.phone || '').toLowerCase().includes(q) ||
      (o.user?.pharmacyName || '').toLowerCase().includes(q) ||
      (o.paymentCode || '').toLowerCase().includes(q.replace(/\s+/g, '')) ||
      (o.orderCode || '').toString().includes(q)
    );
  });

  const orderStatusTone = { PENDING: 'warn', PAID: 'good', EXPIRED: undefined, CANCELLED: undefined, FAILED: 'bad' };
  const orderStatusLabel = {
    PENDING: 'Chờ thanh toán',
    PAID: 'Đã thanh toán',
    EXPIRED: 'Hết hạn',
    CANCELLED: 'Đã hủy',
    FAILED: 'Thất bại',
  };

  const roleLabel = (role) => (role === 'ADMIN' ? 'Quản trị viên' : role === 'INSTRUCTOR' ? 'Giảng viên' : 'Học viên');

  // Cột bảng Khóa học
  const courseColumns = [
    {
      title: 'Khóa học',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src={COURSE_THUMBNAILS[record.slug] || record.thumbnail || '/images/courses/anh_module_sale.png'}
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=100';
            }}
            style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
          />
          <div>
            <div className="adm-cell-title">{text}</div>
            <div className="adm-cell-sub">
              {record.category?.name} · {record.totalLessons || 0} bài học
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <StatusPill tone={status === 'PUBLISHED' ? 'good' : status === 'DRAFT' ? 'warn' : undefined}>
          {status === 'PUBLISHED' ? 'Đang phát hành' : status === 'DRAFT' ? 'Bản nháp' : 'Lưu trữ'}
        </StatusPill>
      ),
    },
    {
      title: 'Cấp độ',
      dataIndex: 'level',
      key: 'level',
      render: (level) => <span className="adm-cell-sub">{level || 'Mọi cấp độ'}</span>,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Space size={6} wrap>
          <Button size="small" icon={<UnorderedListOutlined />} onClick={() => handleOpenManageLessons(record)}>
            Bài học ({record.totalLessons || 0})
          </Button>
          <Button size="small" icon={<PlusOutlined />} onClick={() => handleOpenAddLesson(record)}>
            Thêm bài
          </Button>
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined />}
            aria-label="Xem trang khóa học"
            onClick={() => navigate(`/courses/${record.slug || record.id}`)}
          />
          {isAdmin && (
            <Popconfirm
              title="Xóa khóa học này?"
              description="Tất cả bài học và dữ liệu liên quan sẽ bị xóa vĩnh viễn."
              onConfirm={() => handleDeleteCourse(record.id)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Xóa khóa học" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Cột bảng Học viên
  const userColumns = [
    {
      title: 'Học viên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar src={record.avatar} icon={<UserOutlined />} style={{ backgroundColor: '#005537', flexShrink: 0 }} />
          <div>
            <div className="adm-cell-title">{text}</div>
            <div className="adm-cell-sub">{[record.email, record.phone].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Nhà thuốc và địa bàn',
      dataIndex: 'pharmacyName',
      key: 'pharmacyName',
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name || '—'}</div>
          <div className="adm-cell-sub">
            {[record.province, record.pharmacyScale].filter(Boolean).join(' · ') || 'Chưa có địa bàn'}
          </div>
        </div>
      ),
    },
    {
      title: 'Vị trí',
      dataIndex: 'position',
      key: 'position',
      render: (pos, record) => (
        <div>
          <div>{pos || '—'}</div>
          {record.role !== 'STUDENT' && <div className="adm-cell-sub">{roleLabel(record.role)}</div>}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) =>
        isActive === false ? <StatusPill tone="bad">Đã khóa</StatusPill> : <StatusPill tone="good">Hoạt động</StatusPill>,
    },
    {
      title: '',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size={6} wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleOpenUserDetailModal(record)}>
            Chi tiết
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEditUserModal(record)}>
            Sửa
          </Button>
          <Button size="small" icon={<KeyOutlined />} onClick={() => handleOpenResetPasswordModal(record)}>
            Đổi mật khẩu
          </Button>
          <Popconfirm
            title={record.isActive === false ? 'Mở khóa tài khoản này?' : 'Khóa tài khoản này?'}
            description={
              record.isActive === false
                ? 'Học viên sẽ có thể đăng nhập lại bình thường.'
                : 'Học viên sẽ không thể đăng nhập vào hệ thống cho đến khi được mở khóa lại.'
            }
            onConfirm={() => handleToggleUserStatus(record)}
            okText={record.isActive === false ? 'Mở khóa' : 'Khóa'}
            cancelText="Hủy"
            okButtonProps={{ danger: record.isActive !== false }}
          >
            <Button
              size="small"
              type="text"
              loading={togglingUserId === record.id}
              icon={record.isActive === false ? <UnlockOutlined /> : <LockOutlined />}
              danger={record.isActive !== false}
            >
              {record.isActive === false ? 'Mở khóa' : 'Khóa'}
            </Button>
          </Popconfirm>
          {record.role !== 'ADMIN' && (
            <Popconfirm
              title="Xóa hẳn tài khoản này?"
              description="Xóa luôn gói, tiến độ và bài làm của học viên. Không khôi phục được."
              onConfirm={() => handleDeleteUser(record)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                type="text"
                danger
                loading={deletingUserId === record.id}
                icon={<DeleteOutlined />}
              >
                Xóa
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Cột bảng Gói hội viên: hạn dùng in bằng dấu HSD
  const subscriptionColumns = [
    {
      title: 'Học viên',
      key: 'user',
      render: (_, record) => (
        <div>
          <div className="adm-cell-title">{record.user?.fullName}</div>
          <div className="adm-cell-sub">
            {[record.user?.pharmacyName, record.user?.phone, record.user?.province].filter(Boolean).join(' · ')}
          </div>
        </div>
      ),
    },
    {
      title: 'Gói',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => (
        <div>
          <div className="adm-plan">{plan?.name || 'Gói miễn phí'}</div>
          <div className="adm-plan-price">{formatPrice(plan?.price || 0)}</div>
        </div>
      ),
    },
    {
      title: 'Hạn dùng',
      key: 'duration',
      sorter: (a, b) =>
        (a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity) -
        (b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity),
      render: (_, record) => (
        <div>
          <HsdStamp expiresAt={record.expiresAt} />
          <div className="adm-cell-sub" style={{ marginTop: 4 }}>
            Từ {new Date(record.startsAt).toLocaleDateString('vi-VN')}
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <StatusPill tone={status === 'ACTIVE' ? 'good' : status === 'EXPIRED' ? 'bad' : undefined}>
          {status === 'ACTIVE' ? 'Đang hoạt động' : status === 'EXPIRED' ? 'Hết hạn' : 'Đã hủy'}
        </StatusPill>
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (note) => <span className="adm-cell-sub">{note || '—'}</span>,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Space size={6} wrap>
          <Button size="small" type="primary" ghost icon={<ClockCircleOutlined />} onClick={() => handleOpenExtendModal(record)}>
            Gia hạn
          </Button>
          {record.user && (
            <Button
              size="small"
              type="text"
              icon={<KeyOutlined />}
              aria-label="Đổi mật khẩu"
              title="Đổi mật khẩu"
              onClick={() => handleOpenResetPasswordModal(record.user)}
            />
          )}
        </Space>
      ),
    },
  ];

  // Cột bảng Đơn SePay: mã chuyển khoản in như dòng nội dung chuyển khoản
  const orderColumns = [
    {
      title: 'Học viên',
      key: 'user',
      render: (_, record) => (
        <div>
          <div className="adm-cell-title">{record.user?.fullName}</div>
          <div className="adm-cell-sub">
            {[record.user?.pharmacyName, record.user?.phone || record.user?.email].filter(Boolean).join(' · ')}
          </div>
        </div>
      ),
    },
    {
      title: 'Gói',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => <span className="adm-plan">{plan?.name}</span>,
    },
    {
      title: 'Mã chuyển khoản',
      dataIndex: 'paymentCode',
      key: 'paymentCode',
      // Đơn SePay: mã DTNT... khách ghi trong nội dung chuyển khoản; đơn PayOS cũ: số đơn
      render: (code, record) => (
        <span className={`adm-memo${code ? '' : ' is-legacy'}`}>{code || `#${record.orderCode}`}</span>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amount) => <span className="adm-money">{formatPrice(amount)}</span>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusPill tone={orderStatusTone[status]}>{orderStatusLabel[status] || status}</StatusPill>,
    },
    {
      title: 'Thời gian',
      key: 'time',
      render: (_, record) => (
        <div className="adm-cell-sub" style={{ whiteSpace: 'nowrap' }}>
          <div>Tạo {new Date(record.createdAt).toLocaleString('vi-VN')}</div>
          {record.paidAt && <div>Trả {new Date(record.paidAt).toLocaleString('vi-VN')}</div>}
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Button
          size="small"
          disabled={!record.rawWebhookPayload}
          title={record.rawWebhookPayload ? undefined : 'Chưa có dữ liệu giao dịch từ ngân hàng'}
          onClick={() => setSelectedOrderPayload(record)}
        >
          Đối soát
        </Button>
      ),
    },
  ];

  // Việc đang chờ: hiện thành số màu hổ phách trên thanh bên
  const pendingOrders = orderStats?.statusCounts?.PENDING || 0;
  const failedOrders =
    (orderStats?.statusCounts?.FAILED || 0) +
    (orderStats?.statusCounts?.EXPIRED || 0) +
    (orderStats?.statusCounts?.CANCELLED || 0);
  // Gói miễn phí đã hết hạn không cần theo dõi: ẩn khỏi bảng và không tính vào số cần gia hạn
  const listedSubs = subscriptions.filter((s) => {
    const isFree = !s.plan?.price;
    const isExpired = s.status === 'EXPIRED' || (s.expiresAt && daysUntil(s.expiresAt) < 0);
    return !(isFree && isExpired);
  });
  const activeSubs = listedSubs.filter((s) => s.status === 'ACTIVE');
  const overdueSubs = activeSubs.filter((s) => s.expiresAt && daysUntil(s.expiresAt) < 0);
  const expiringSubs = activeSubs.filter((s) => {
    const left = s.expiresAt ? daysUntil(s.expiresAt) : null;
    return left !== null && left >= 0 && left <= 7;
  });

  const visibleOrders = filteredOrders.filter((o) => {
    if (orderStatusFilter === 'all') return true;
    if (orderStatusFilter === 'failed') return ['FAILED', 'EXPIRED', 'CANCELLED'].includes(o.status);
    return o.status === orderStatusFilter;
  });

  const visibleSubscriptions =
    subFilter === 'expiring'
      ? [...overdueSubs, ...expiringSubs].sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))
      : subFilter === 'expired'
      ? listedSubs.filter((s) => s.status === 'EXPIRED')
      : listedSubs;

  // Danh mục thanh bên (giảng viên chỉ thấy mục trong INSTRUCTOR_MENU_KEYS)
  const INSTRUCTOR_MENU_KEYS = ['courses', 'analytics'];
  const allSections = [
    {
      key: 'orders',
      group: 'Doanh thu',
      title: 'Đơn SePay',
      icon: <CreditCardOutlined />,
      pending: pendingOrders,
      pendingHint: 'Đơn chờ thanh toán',
    },
    {
      key: 'subscriptions',
      group: 'Doanh thu',
      title: 'Gói hội viên',
      icon: <CrownOutlined />,
      pending: expiringSubs.length + overdueSubs.length,
      pendingHint: 'Gói quá hạn hoặc hết hạn trong 7 ngày',
    },
    { key: 'courses', group: 'Nội dung', title: 'Khóa học', icon: <BookOutlined /> },
    { key: 'users', group: 'Khách hàng', title: 'Học viên', icon: <TeamOutlined /> },
    // Chỉ ADMIN: không nằm trong INSTRUCTOR_MENU_KEYS (YC-ADM-08)
    { key: 'leads', group: 'Khách hàng', title: 'Lead công cụ', icon: <ContactsOutlined /> },
    // Chỉ ADMIN: xem lịch sử khung chat hỗ trợ, chiếm quyền chat với khách
    {
      key: 'support-chat',
      group: 'Khách hàng',
      title: 'Chat hỗ trợ',
      icon: <CustomerServiceOutlined />,
      pending: waitingChats,
      pendingHint: 'Cuộc chat đang chờ người trả lời',
    },
    { key: 'analytics', group: 'Báo cáo', title: 'Thống kê', icon: <BarChartOutlined /> },
  ];
  const sections = isAdmin ? allSections : allSections.filter((s) => INSTRUCTOR_MENU_KEYS.includes(s.key));

  const openGrantModal = () => {
    grantForm.resetFields();
    setIsGrantModalOpen(true);
  };

  const handleLogout = () => {
    logout();
    toast.success('Đã đăng xuất khỏi hệ thống!');
    navigate('/login');
  };

  const topActions = {
    subscriptions: (
      <Button type="primary" icon={<PlusOutlined />} onClick={openGrantModal}>
        Cấp gói thủ công
      </Button>
    ),
    courses: (
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCourseModalOpen(true)}>
        Tạo khóa học
      </Button>
    ),
  };

  const tableProps = { rowKey: 'id', size: 'middle', scroll: { x: 'max-content' }, pagination: { pageSize: 10, hideOnSinglePage: true } };

  return (
    <ConfigProvider theme={adminTheme}>
      <AdminShell
        sections={sections}
        activeKey={activeMenuKey}
        onSelect={setActiveMenuKey}
        actions={topActions[activeMenuKey]}
        user={currentUser}
        roleLabel={isAdmin ? 'Quản trị viên' : 'Giảng viên'}
        onViewSite={() => navigate('/courses')}
        onLogout={handleLogout}
      >
        {loading ? (
          <div className="adm-panel adm-panel-pad">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : (
          <>
            {activeMenuKey === 'orders' && (
              <>
                <KpiStrip
                  items={[
                    {
                      label: 'Doanh thu đã thu',
                      value: formatPrice(orderStats?.totalRevenue || 0),
                      tone: 'good',
                      trend: <RevenueTrend orders={orders} />,
                    },
                    { label: 'Đã thanh toán', value: orderStats?.statusCounts?.PAID || 0 },
                    { label: 'Chờ thanh toán', value: pendingOrders, tone: pendingOrders ? 'warn' : undefined },
                    { label: 'Thất bại / hết hạn', value: failedOrders, tone: failedOrders ? 'bad' : undefined },
                  ]}
                />
                <Panel
                  tools={
                    <Space wrap size={10}>
                      <Input
                        className="adm-search"
                        placeholder="Tìm mã chuyển khoản, tên, SĐT, nhà thuốc"
                        prefix={<SearchOutlined />}
                        value={orderSearchText}
                        onChange={(e) => setOrderSearchText(e.target.value)}
                        allowClear
                      />
                      <Segmented
                        value={orderStatusFilter}
                        onChange={setOrderStatusFilter}
                        options={[
                          { label: 'Tất cả', value: 'all' },
                          { label: `Chờ thanh toán${pendingOrders ? ` · ${pendingOrders}` : ''}`, value: 'PENDING' },
                          { label: 'Đã thanh toán', value: 'PAID' },
                          { label: 'Lỗi / hết hạn', value: 'failed' },
                        ]}
                      />
                    </Space>
                  }
                  meta={`${visibleOrders.length} / ${orders.length} đơn`}
                >
                  <Table
                    {...tableProps}
                    columns={orderColumns}
                    dataSource={visibleOrders}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            orders.length
                              ? 'Không có đơn khớp bộ lọc. Thử xóa từ khóa hoặc chọn Tất cả.'
                              : 'Chưa có đơn nào. Đơn xuất hiện khi học viên bấm mua gói ở trang thanh toán.'
                          }
                        />
                      ),
                    }}
                  />
                </Panel>
              </>
            )}

            {activeMenuKey === 'subscriptions' && (
              <>
                <KpiStrip
                  items={[
                    { label: 'Doanh thu thu phí', value: formatPrice(subStats?.totalRevenue || 0), tone: 'good' },
                    {
                      label: 'Gói đang hoạt động',
                      value: subStats?.activeSubscriptions || activeSubs.length,
                      note: `VIP Toàn Diện ${subStats?.planCounts?.HOI_VIEN_TOAN_DIEN || 0} · Tăng Trưởng & Khởi Nghiệp ${
                        (subStats?.planCounts?.TANG_TRUONG || 0) + (subStats?.planCounts?.KHOI_NGHIEP || 0)
                      }`,
                    },
                    {
                      label: 'Hết hạn trong 7 ngày',
                      value: expiringSubs.length,
                      tone: expiringSubs.length ? 'warn' : undefined,
                    },
                    {
                      label: 'Quá hạn, chưa gia hạn',
                      value: overdueSubs.length,
                      tone: overdueSubs.length ? 'bad' : undefined,
                    },
                  ]}
                />
                <Panel
                  tools={
                    <Segmented
                      value={subFilter}
                      onChange={setSubFilter}
                      options={[
                        { label: 'Tất cả', value: 'all' },
                        {
                          label: `Cần gia hạn${
                            expiringSubs.length + overdueSubs.length ? ` · ${expiringSubs.length + overdueSubs.length}` : ''
                          }`,
                          value: 'expiring',
                        },
                        { label: 'Đã hết hạn', value: 'expired' },
                      ]}
                    />
                  }
                  meta={`${visibleSubscriptions.length} / ${listedSubs.length} gói`}
                >
                  <Table
                    {...tableProps}
                    columns={subscriptionColumns}
                    dataSource={visibleSubscriptions}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            subFilter === 'expiring'
                              ? 'Không có gói nào cần gia hạn trong 7 ngày tới.'
                              : 'Chưa có gói hội viên. Bấm Cấp gói thủ công để kích hoạt cho học viên đã chuyển khoản.'
                          }
                        />
                      ),
                    }}
                  />
                </Panel>
              </>
            )}

            {activeMenuKey === 'courses' && (
              <Panel
                title={isAdmin ? 'Tất cả khóa học' : 'Khóa học của tôi'}
                meta={`${courses.length} khóa${stats?.totalLessons ? ` · ${stats.totalLessons} bài học` : ''}`}
              >
                <Table
                  {...tableProps}
                  columns={courseColumns}
                  dataSource={courses}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Chưa có khóa học. Bấm Tạo khóa học để bắt đầu, rồi thêm bài học vào khóa."
                      />
                    ),
                  }}
                />
              </Panel>
            )}

            {activeMenuKey === 'users' && (
              <Panel
                tools={
                  <Input
                    className="adm-search"
                    placeholder="Tìm tên, email, SĐT, nhà thuốc, tỉnh"
                    prefix={<SearchOutlined />}
                    value={userSearchText}
                    onChange={(e) => setUserSearchText(e.target.value)}
                    allowClear
                  />
                }
                meta={`${filteredUsers.length} / ${users.length} tài khoản`}
              >
                <Table
                  {...tableProps}
                  columns={userColumns}
                  dataSource={filteredUsers}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={users.length ? 'Không có tài khoản khớp từ khóa.' : 'Chưa có tài khoản nào.'}
                      />
                    ),
                  }}
                />
              </Panel>
            )}

            {/* Lead công cụ, chat hỗ trợ: chỉ ADMIN */}
            {activeMenuKey === 'leads' && isAdmin && <AdminLeadsPanel />}
            {activeMenuKey === 'support-chat' && isAdmin && <AdminSupportChatPanel onWaitingChange={setWaitingChats} />}

            {activeMenuKey === 'analytics' && (
              <>
                <KpiStrip
                  items={[
                    { label: isAdmin ? 'Tổng khóa học' : 'Khóa học của tôi', value: stats?.totalCourses || courses.length },
                    {
                      label: isAdmin ? 'Học viên' : 'Học viên ghi danh',
                      value: isAdmin
                        ? stats?.totalStudents || users.filter((u) => u.role === 'STUDENT').length
                        : stats?.totalStudents || 0,
                    },
                    {
                      label: isAdmin ? 'Gói hội viên đã cấp' : 'Lượt ghi danh',
                      value: isAdmin ? subscriptions.length : stats?.totalEnrollments || 0,
                    },
                    { label: 'Tỷ lệ hoàn thành', value: stats?.overallCompletionRate || '0%', tone: 'good' },
                  ]}
                />
                {isAdmin && (
                  <Panel title="Bài học được hoàn thành nhiều nhất" pad>
                    {/* Số thật từ GET /analytics/dashboard (topCompletedLessons) — HT-06 F-14 */}
                    {stats?.topCompletedLessons?.length > 0 ? (
                      <ol className="adm-ranked">
                        {stats.topCompletedLessons.map((l) => (
                          <li key={l.id}>
                            {l.title}: <b>{l.completions}</b> lượt hoàn thành
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="adm-empty-note">Chưa có học viên nào hoàn thành bài học.</p>
                    )}
                  </Panel>
                )}
              </>
            )}
          </>
        )}
      </AdminShell>

      {/* ==================== MODAL QUẢN LÝ & XÓA BÀI HỌC TRONG KHÓA ==================== */}
      <Modal
        rootClassName="adm-modal"
        title={`Bài học trong khóa: ${selectedCourseForManage?.title || ''}`}
        open={isManageLessonsModalOpen}
        onCancel={() => setIsManageLessonsModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsManageLessonsModalOpen(false)}>
            Đóng
          </Button>,
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              handleOpenAddLesson(selectedCourseForManage);
            }}
          >
            Thêm bài học
          </Button>,
        ]}
        width={750}
      >
        <div style={{ margin: '16px 0' }}>
          <Table
            loading={loadingCourseLessons}
            dataSource={courseLessonsList}
            rowKey="id"
            pagination={{ pageSize: 6 }}
            bordered
            columns={[
              {
                title: 'Mã & Bài Học',
                key: 'lesson',
                render: (_, l) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {l.code && <span className="adm-lesson-code">{l.code}</span>}
                    <span className="adm-cell-title">
                      {l.title.replace(/^[A-Z0-9]+\s*—\s*/, '')}
                    </span>
                  </div>
                ),
              },
              {
                title: 'Thời lượng',
                key: 'duration',
                width: 100,
                render: (_, l) => `${l.estimatedMinutes || 40} phút`,
              },
              {
                title: 'Tài liệu',
                key: 'attachments',
                width: 100,
                render: (_, l) => {
                  const count = l.attachmentsCount || 0;
                  return count > 0 ? `${count} tệp` : <span className="adm-cell-sub">Không có</span>;
                },
              },
              {
                title: 'Hành động',
                key: 'action',
                width: 130,
                render: (_, l) => (
                  <Space size={6}>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => navigate(`/learn/${selectedCourseForManage.id}/lessons/${l.id}`)}
                    >
                      Học
                    </Button>
                    <Popconfirm
                      title="Xác nhận xóa bài học này?"
                      description={`Bạn có chắc chắn muốn xóa bài học "${l.title}"?`}
                      onConfirm={() => handleDeleteLessonInCourse(l.id)}
                      okText="Xóa"
                      cancelText="Hủy"
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} title="Xóa bài học này" />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </Modal>

      {/* Modal Kích Hoạt / Cấp Gói Học Viên */}
      <Modal
        rootClassName="adm-modal"
        title="Cấp gói hội viên thủ công"
        open={isGrantModalOpen}
        onCancel={() => setIsGrantModalOpen(false)}
        footer={null}
        width={560}
      >
        <Form form={grantForm} layout="vertical" onFinish={handleGrantSubscription} style={{ marginTop: 16 }}>
          <Form.Item
            label="Chọn Học viên / Dược sĩ"
            name="userId"
            rules={[{ required: true, message: 'Vui lòng chọn học viên' }]}
          >
            <Select
              showSearch
              placeholder="Tìm theo tên hoặc SĐT..."
              optionFilterProp="children"
            >
              {users.map((u) => (
                <Select.Option key={u.id} value={u.id}>
                  {u.fullName} ({u.email} - {u.phone || 'Chưa có SĐT'})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Gói Cước Muốn Cấp"
            name="planId"
            rules={[{ required: true, message: 'Vui lòng chọn gói cước' }]}
          >
            <Select placeholder="Chọn gói cước">
              {plans.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.name} — {formatPrice(p.price)} ({p.durationMonths} tháng)
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Thời hạn tùy chỉnh (tháng)" name="durationMonths" initialValue={12}>
            <InputNumber min={1} max={60} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Ghi chú thanh toán" name="note" initialValue="Đã xác nhận chuyển khoản ngân hàng">
            <Input placeholder="Ví dụ: Đã nhận 4.990.000đ qua MB Bank" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <Button onClick={() => setIsGrantModalOpen(false)}>Hủy</Button>
            <Button type="primary" htmlType="submit" loading={granting}>
              Kích hoạt gói
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal Gia Hạn Gói */}
      <Modal
        rootClassName="adm-modal"
        title="Gia hạn gói hội viên"
        open={isExtendModalOpen}
        onCancel={() => setIsExtendModalOpen(false)}
        footer={null}
        width={480}
      >
        <Form form={extendForm} layout="vertical" onFinish={handleExtendSubscription} style={{ marginTop: 16 }}>
          <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>
            Học viên: <strong>{selectedSubForExtend?.user?.fullName}</strong> ({selectedSubForExtend?.plan?.name})
          </p>

          <Form.Item label="Cộng thêm số tháng" name="extendMonths" initialValue={6}>
            <InputNumber min={1} max={36} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Trạng thái gói" name="status" initialValue="ACTIVE">
            <Select>
              <Select.Option value="ACTIVE">Đang hoạt động (ACTIVE)</Select.Option>
              <Select.Option value="EXPIRED">Hết hạn (EXPIRED)</Select.Option>
              <Select.Option value="CANCELLED">Hủy (CANCELLED)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Ghi chú gia hạn" name="note">
            <Input placeholder="Ví dụ: Đã gia hạn thêm 6 tháng" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <Button onClick={() => setIsExtendModalOpen(false)}>Hủy</Button>
            <Button type="primary" htmlType="submit" loading={extending}>
              Lưu gia hạn
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal Tạo Khóa Học Mới */}
      <Modal
        rootClassName="adm-modal"
        title="Tạo khóa học mới"
        open={isCourseModalOpen}
        onCancel={() => setIsCourseModalOpen(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={courseForm} layout="vertical" onFinish={handleCreateCourse} style={{ marginTop: 16 }}>
          <Form.Item
            label="Tiêu đề khóa học"
            name="title"
            rules={[{ required: true, message: 'Vui lòng nhập tên khóa học' }]}
          >
            <Input placeholder="Ví dụ: Kỹ Năng Tư Vấn Thuốc Kê Đơn & Bệnh Mãn Tính" />
          </Form.Item>

          <Form.Item
            label="Chuyên đề đào tạo"
            name="categoryId"
            rules={[{ required: true, message: 'Vui lòng chọn chuyên đề' }]}
          >
            <Select placeholder="Chọn chuyên đề">
              {categories.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Mô tả ngắn gọn"
            name="shortDescription"
            rules={[{ required: true, message: 'Vui lòng nhập mô tả ngắn' }]}
          >
            <Input.TextArea rows={2} placeholder="Tóm tắt ngắn gọn nội dung và mục tiêu khóa học..." />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Thời lượng ước tính (giờ)" name="estimatedHours" initialValue={3}>
                <InputNumber min={0.5} max={50} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Cấp độ" name="level" initialValue="ALL">
                <Select>
                  <Select.Option value="ALL">Mọi cấp độ</Select.Option>
                  <Select.Option value="BEGINNER">Cơ bản</Select.Option>
                  <Select.Option value="INTERMEDIATE">Trung cấp</Select.Option>
                  <Select.Option value="ADVANCED">Nâng cao</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Ảnh Thumbnail (URL)"
            name="thumbnail"
            initialValue="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800"
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <Button onClick={() => setIsCourseModalOpen(false)}>Hủy</Button>
            <Button type="primary" htmlType="submit" loading={creatingCourse}>
              Tạo khóa học
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal Thêm Bài Học Mới */}
      <Modal
        rootClassName="adm-modal"
        title={`Thêm bài học: ${selectedCourseForLesson?.title || ''}`}
        open={isLessonModalOpen}
        onCancel={() => setIsLessonModalOpen(false)}
        footer={null}
        width={650}
        destroyOnClose
      >
        <Form form={lessonForm} layout="vertical" onFinish={handleCreateLesson} style={{ marginTop: 16 }}>
          <Form.Item
            label="Tên bài học"
            name="title"
            rules={[{ required: true, message: 'Vui lòng nhập tên bài học' }]}
          >
            <Input placeholder="Ví dụ: Quy trình 6 bước tư vấn an toàn tại quầy" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Loại bài giảng" name="lessonType" initialValue="ARTICLE">
                <Select>
                  <Select.Option value="ARTICLE">Tài liệu / Văn bản hướng dẫn</Select.Option>
                  <Select.Option value="VIDEO">Video bài giảng</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Thời lượng ước tính (phút)" name="estimatedMinutes" initialValue={45}>
                <InputNumber min={5} max={300} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Mã Module bài học</span>
                {suggestedCode && (
                  <span className="adm-lesson-code">Tự sinh: {suggestedCode}</span>
                )}
              </div>
            }
            name="code"
            extra="Mã module được hệ thống tự động sinh và khóa cố định theo tiến trình chuẩn hóa của khóa học (+1)."
          >
            <Input
              disabled
              placeholder={suggestedCode || 'Ví dụ: S1, C5...'}
              style={{
                fontWeight: 700,
                color: '#005537',
                fontSize: 14,
                cursor: 'not-allowed',
              }}
            />
          </Form.Item>

          <Form.Item label="Nội dung bài giảng (Markdown)" name="contentMarkdown" initialValue="## Nội dung bài giảng...">
            <Input.TextArea rows={6} placeholder="Nhập nội dung bài học..." />
          </Form.Item>

          {/* Section: Danh Sách Tài Liệu Đính Kèm (Công Cụ / Bảng Tính / File Thực Hành) */}
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                  Tài Liệu & Công Cụ Thực Hành Đính Kèm
                </span>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Thêm file công cụ, mẫu biểu (Excel, Word, PDF, Markdown) để bài học có tag tài liệu và học viên có thể tải về.
                </div>
              </div>
            </div>

            <Form.List name="attachments">
              {(fields, { add, remove }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ background: '#f6f8f5', borderColor: '#dfe6e1', borderRadius: 10 }}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                          <PaperClipOutlined />
                          <span>Tài liệu #{index + 1}</span>
                        </div>
                      }
                      extra={
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        >
                          Xóa
                        </Button>
                      }
                    >
                      <Row gutter={12}>
                        <Col span={14}>
                          <Form.Item
                            {...restField}
                            label="Tên tài liệu / Biểu mẫu"
                            name={[name, 'name']}
                            rules={[{ required: true, message: 'Nhập tên tài liệu' }]}
                          >
                            <Input placeholder="Ví dụ: Checklist kiểm chứng thông tin thuốc" />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            {...restField}
                            label="Định dạng file"
                            name={[name, 'format']}
                            initialValue="Excel"
                          >
                            <Select>
                              <Select.Option value="Excel">Bảng tính Excel / CSV</Select.Option>
                              <Select.Option value="PDF">Tài liệu PDF</Select.Option>
                              <Select.Option value="Word">Văn bản Word</Select.Option>
                              <Select.Option value="Markdown">Bài đọc file Markdown</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            label={
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>Mã tài liệu</span>
                                <span className="adm-lesson-code">
                                  {`${suggestedCode || 'D5'}-TL${String(index + 1).padStart(2, '0')}`}
                                </span>
                              </div>
                            }
                            name={[name, 'code']}
                            initialValue={`${suggestedCode || 'D5'}-TL${String(index + 1).padStart(2, '0')}`}
                          >
                            <Input
                              disabled
                              placeholder={`${suggestedCode || 'D5'}-TL${String(index + 1).padStart(2, '0')}`}
                              style={{
                                fontWeight: 700,
                                color: '#005537',
                                fontSize: 13,
                                cursor: 'not-allowed',
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            label="Link tải file / URL (nếu có)"
                            name={[name, 'fileUrl']}
                          >
                            <Input placeholder="https://..." />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        {...restField}
                        label="Nội dung tài liệu (Markdown / Bảng tính để xem & tải trực tiếp)"
                        name={[name, 'contentMarkdown']}
                      >
                        <Input.TextArea
                          rows={3}
                          placeholder="Nhập nội dung chi tiết của tài liệu, bảng mẫu hoặc checklist..."
                        />
                      </Form.Item>
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        format: 'Excel',
                        code: `${suggestedCode || 'TL'}-TL0${fields.length + 1}`,
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                  >
                    Thêm tài liệu đính kèm
                  </Button>
                </div>
              )}
            </Form.List>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <Button onClick={() => setIsLessonModalOpen(false)}>Hủy</Button>
            <Button type="primary" htmlType="submit" loading={creatingLesson}>
              Thêm bài học
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ==================== MODAL XÓA TÀI KHOẢN CÓ ĐƠN ĐÃ THANH TOÁN (TÀI KHOẢN THỬ NGHIỆM) ==================== */}
      <Modal
        rootClassName="adm-modal"
        title="Xóa tài khoản kèm đơn hàng"
        open={!!forceDeleteUser}
        onCancel={() => setForceDeleteUser(null)}
        onOk={() => handleDeleteUser(forceDeleteUser, forceDeleteEmail)}
        okText="Xóa vĩnh viễn"
        cancelText="Hủy"
        okButtonProps={{
          danger: true,
          loading: deletingUserId === forceDeleteUser?.id,
          disabled: forceDeleteEmail.trim().toLowerCase() !== (forceDeleteUser?.email || '').toLowerCase(),
        }}
        destroyOnClose
      >
        {forceDeleteUser && (
          <div>
            <p>
              Tài khoản <strong>{forceDeleteUser.fullName || forceDeleteUser.email}</strong> có{' '}
              <strong>{forceDeleteUser.paidOrders} đơn đã thanh toán</strong>. Xóa sẽ mất luôn các đơn này khỏi sổ sách
              và doanh thu. Chỉ làm với tài khoản thử nghiệm.
            </p>
            <p>
              Gõ lại email <strong>{forceDeleteUser.email}</strong> để xác nhận:
            </p>
            <Input
              value={forceDeleteEmail}
              onChange={(e) => setForceDeleteEmail(e.target.value)}
              placeholder={forceDeleteUser.email}
              autoComplete="off"
            />
          </div>
        )}
      </Modal>

      {/* ==================== MODAL CẬP NHẬT MẬT KHẨU CHO HỌC VIÊN / NHÀ THUỐC ==================== */}
      <Modal
        rootClassName="adm-modal"
        title="Đặt lại mật khẩu"
        open={isResetPasswordModalOpen}
        onCancel={() => {
          setIsResetPasswordModalOpen(false);
          resetPasswordForm.resetFields();
        }}
        footer={null}
        width={540}
        destroyOnClose
      >
        {selectedUserForReset && (
          <div style={{ padding: '8px 0' }}>
            {/* User info banner */}
            <div className="adm-person">
              <Avatar
                src={selectedUserForReset.avatar}
                icon={<UserOutlined />}
                size={48}
                style={{ backgroundColor: '#005537' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
                    {selectedUserForReset.fullName}
                  </span>
                  <span className="adm-cell-sub">{roleLabel(selectedUserForReset.role)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: '#475569', marginTop: 2 }}>
                  {[selectedUserForReset.email, selectedUserForReset.phone].filter(Boolean).join(' · ')}
                </div>
                {selectedUserForReset.pharmacyName && (
                  <div style={{ fontSize: 12, color: '#005537', marginTop: 2, fontWeight: 500 }}>
                    <MedicineBoxOutlined /> {selectedUserForReset.pharmacyName}
                  </div>
                )}
              </div>
            </div>

            <Alert
              message="Lưu ý quản trị viên"
              description="Sau khi đặt lại, học viên / đại diện nhà thuốc có thể đăng nhập ngay bằng mật khẩu mới này. Bạn hãy sao chép hoặc ghi nhớ để gửi cho học viên."
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 8, fontSize: 12.5 }}
            />

            <Form
              form={resetPasswordForm}
              layout="vertical"
              onFinish={handleResetPasswordSubmit}
            >
              <Form.Item
                label={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontWeight: 600 }}>Mật khẩu mới</span>
                    <Button
                      type="link"
                      size="small"
                      icon={<ThunderboltOutlined />}
                      onClick={handleGenerateRandomPassword}
                      style={{ padding: 0, height: 'auto', fontSize: 12 }}
                    >
                      Tạo mật khẩu ngẫu nhiên
                    </Button>
                  </div>
                }
                name="newPassword"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu mới' },
                  { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' },
                ]}
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Nhập mật khẩu mới (tối thiểu 6 ký tự)..."
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontWeight: 600 }}>Xác nhận mật khẩu mới</span>}
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Vui lòng xác nhận mật khẩu mới' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  size="large"
                  prefix={<SafetyCertificateOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Nhập lại mật khẩu mới..."
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
                <Button
                  onClick={() => {
                    setIsResetPasswordModalOpen(false);
                    resetPasswordForm.resetFields();
                  }}
                  style={{ borderRadius: 8 }}
                >
                  Hủy
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={resettingPassword}
                  icon={<KeyOutlined />}
                >
                  Đặt mật khẩu mới
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Modal>

      {/* ==================== MODAL XEM CHI TIẾT HỌC VIÊN / NHÀ THUỐC ==================== */}
      <Modal
        rootClassName="adm-modal"
        title="Chi tiết tài khoản"
        open={isUserDetailModalOpen}
        onCancel={() => {
          setIsUserDetailModalOpen(false);
          setSelectedUserForDetail(null);
        }}
        footer={
          <Button
            onClick={() => {
              setIsUserDetailModalOpen(false);
              setSelectedUserForDetail(null);
            }}
          >
            Đóng
          </Button>
        }
        width={860}
        destroyOnClose
      >
        {loadingUserDetail ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : selectedUserForDetail ? (
          <div style={{ padding: '8px 0' }}>
            <div className="adm-person">
              <Avatar
                src={selectedUserForDetail.avatar}
                icon={<UserOutlined />}
                size={56}
                style={{ backgroundColor: '#005537' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>
                    {selectedUserForDetail.fullName}
                  </span>
                  <span className="adm-cell-sub">{roleLabel(selectedUserForDetail.role)}</span>
                  {selectedUserForDetail.isActive === false ? (
                    <StatusPill tone="bad">Đã khóa</StatusPill>
                  ) : (
                    <StatusPill tone="good">Hoạt động</StatusPill>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4 }}>
                  <MailOutlined /> {selectedUserForDetail.email}
                  {selectedUserForDetail.phone ? <> &nbsp;•&nbsp; <PhoneOutlined /> {selectedUserForDetail.phone}</> : null}
                </div>
              </div>
            </div>

            <Descriptions
              bordered
              column={1}
              size="small"
              styles={{ label: { width: 200, fontWeight: 600, color: '#4b5d55' } }}
            >
              <Descriptions.Item label={<><MedicineBoxOutlined /> Nhà thuốc / Quầy thuốc</>}>
                {selectedUserForDetail.pharmacyName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Vị trí công tác">
                {selectedUserForDetail.position || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={<><EnvironmentOutlined /> Địa bàn</>}>
                {[selectedUserForDetail.district, selectedUserForDetail.province].filter(Boolean).join(', ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Quy mô quầy / nhà thuốc">
                {selectedUserForDetail.pharmacyScale || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={<><PhoneOutlined /> Số Zalo</>}>
                {selectedUserForDetail.zaloNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Nguồn Lead / Giới thiệu">
                {selectedUserForDetail.leadSource || '—'}
              </Descriptions.Item>
              {selectedUserForDetail.bio && (
                <Descriptions.Item label="Ghi chú / Tiểu sử">
                  {selectedUserForDetail.bio}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={<><CalendarOutlined /> Ngày tạo tài khoản</>}>
                {selectedUserForDetail.createdAt
                  ? new Date(selectedUserForDetail.createdAt).toLocaleString('vi-VN')
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            {selectedUserForDetail.id && <AdminUserQuizResults userId={selectedUserForDetail.id} />}

            {Array.isArray(selectedUserForDetail.subscriptions) && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Gói hội viên</div>
                {selectedUserForDetail.subscriptions.length === 0 ? (
                  <Empty description="Chưa có gói hội viên nào" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {selectedUserForDetail.subscriptions.map((sub) => (
                      <div
                        key={sub.id}
                        style={{
                          border: '1px solid #dfe6e1',
                          borderRadius: 8,
                          padding: '10px 12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{sub.plan?.name || 'Gói không xác định'}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {sub.startDate ? new Date(sub.startDate).toLocaleDateString('vi-VN') : ''}
                            {sub.endDate ? ` → ${new Date(sub.endDate).toLocaleDateString('vi-VN')}` : ''}
                          </div>
                        </div>
                        <StatusPill tone={sub.status === 'ACTIVE' ? 'good' : sub.status === 'EXPIRED' ? 'bad' : undefined}>
                          {sub.status === 'ACTIVE' ? 'Đang hoạt động' : sub.status === 'EXPIRED' ? 'Hết hạn' : 'Đã hủy'}
                        </StatusPill>
                      </div>
                    ))}
                  </Space>
                )}
              </div>
            )}
          </div>
        ) : (
          <Empty description="Không có dữ liệu" />
        )}
      </Modal>

      {/* ==================== MODAL SỬA THÔNG TIN HỌC VIÊN / NHÀ THUỐC ==================== */}
      <Modal
        rootClassName="adm-modal"
        title="Sửa thông tin học viên"
        open={isEditUserModalOpen}
        onCancel={() => {
          setIsEditUserModalOpen(false);
          editUserForm.resetFields();
        }}
        footer={null}
        width={640}
        destroyOnClose
      >
        {selectedUserForEdit && (
          <Form form={editUserForm} layout="vertical" onFinish={handleUpdateUserSubmit}>
            <Alert
              message={`Email: ${selectedUserForEdit.email}`}
              description="Email và mật khẩu không thể sửa tại đây. Dùng chức năng Đổi mật khẩu để đặt lại mật khẩu."
              type="info"
              showIcon
              style={{ marginBottom: 16, borderRadius: 8, fontSize: 12.5 }}
            />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Họ và tên"
                  name="fullName"
                  rules={[
                    { required: true, message: 'Vui lòng nhập họ tên' },
                    { min: 2, message: 'Họ tên phải có ít nhất 2 ký tự' },
                  ]}
                >
                  <Input placeholder="Nguyễn Văn A" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Số điện thoại" name="phone">
                  <Input placeholder="09xxxxxxxx" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Vai trò" name="role" rules={[{ required: true, message: 'Chọn vai trò' }]}>
                  <Select
                    options={[
                      { value: 'STUDENT', label: 'Học viên' },
                      { value: 'INSTRUCTOR', label: 'Giảng viên' },
                      { value: 'ADMIN', label: 'Quản trị viên' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Vị trí công tác" name="position">
                  <Input placeholder="Dược sĩ bán hàng" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Tên nhà thuốc / quầy thuốc" name="pharmacyName">
                  <Input placeholder="Nhà thuốc ABC" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Quy mô quầy / nhà thuốc" name="pharmacyScale">
                  <Input placeholder="Quầy 1 người / Chuỗi 2-5 điểm..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Tỉnh / Thành phố" name="province">
                  <Select
                    placeholder="Chọn Tỉnh / Thành"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={PROVINCE_OPTIONS}
                    onChange={handleEditProvinceChange}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Phường / Xã" name="district">
                  <Select
                    placeholder="Chọn Tỉnh/Thành trước"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    disabled={editWardOptions.length === 0}
                    notFoundContent="Vui lòng chọn Tỉnh/Thành trước"
                    options={editWardOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Số Zalo" name="zaloNumber">
                  <Input placeholder="09xxxxxxxx" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Nguồn Lead / Giới thiệu" name="leadSource">
                  <Input placeholder="Facebook, Zalo, Giới thiệu..." />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <Button
                onClick={() => {
                  setIsEditUserModalOpen(false);
                  editUserForm.resetFields();
                }}
                style={{ borderRadius: 8 }}
              >
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={updatingUser}
              >
                Lưu thay đổi
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      {/* ==================== MODAL XEM PAYLOAD WEBHOOK (ĐỐI SOÁT ĐƠN HÀNG) ==================== */}
      <Modal
        rootClassName="adm-modal"
        title={`Đối soát giao dịch ${selectedOrderPayload?.paymentCode || `#${selectedOrderPayload?.orderCode || ''}`}`}
        open={!!selectedOrderPayload}
        onCancel={() => setSelectedOrderPayload(null)}
        footer={
          <Button onClick={() => setSelectedOrderPayload(null)} style={{ borderRadius: 8 }}>
            Đóng
          </Button>
        }
        width={640}
        destroyOnClose
      >
        {selectedOrderPayload && (
          <div>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Học viên">
                {selectedOrderPayload.user?.fullName} ({selectedOrderPayload.user?.email})
              </Descriptions.Item>
              <Descriptions.Item label="Gói">{selectedOrderPayload.plan?.name}</Descriptions.Item>
              <Descriptions.Item label="Số tiền">{formatPrice(selectedOrderPayload.amount)}</Descriptions.Item>
              {selectedOrderPayload.paidAmount != null && (
                <Descriptions.Item label="Thực nhận">{formatPrice(selectedOrderPayload.paidAmount)}</Descriptions.Item>
              )}
              <Descriptions.Item label="Trạng thái">
                <StatusPill tone={orderStatusTone[selectedOrderPayload.status]}>
                  {orderStatusLabel[selectedOrderPayload.status] || selectedOrderPayload.status}
                </StatusPill>
              </Descriptions.Item>
              {selectedOrderPayload.failReason && (
                <Descriptions.Item label="Lý do lỗi">{selectedOrderPayload.failReason}</Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>
              Dữ liệu giao dịch gốc ({selectedOrderPayload.provider === 'PAYOS' ? 'PayOS' : 'SePay'}):
            </div>
            <pre className="adm-json">
              {JSON.stringify(selectedOrderPayload.rawWebhookPayload, null, 2)}
            </pre>
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
}
